import { useAppKit } from "@reown/appkit/react";
import { useState, useEffect, useRef } from "react";
import { useDeviceDetection } from "app/hooks/useDeviceDetection";
import { useMetaMaskConnection } from "app/hooks/useMetaMaskConnection";
import { setUserManuallyDisconnected } from "app/hooks/useConnect";
import ethereumProviderGuard from "app/utils/ethereum-provider-guard";
import Button from "../button/button";
import Notification from "../notification/notification";
import MetaMaskErrorRecovery from "../metamask-error-recovery/MetaMaskErrorRecovery";
import OKXErrorRecovery from "../okx-error-recovery/OKXErrorRecovery";
import PhantomErrorRecovery from "../phantom-error-recovery/PhantomErrorRecovery";
import WalletConflictWarning from "../wallet-conflict-warning/WalletConflictWarning";
import {
  useWalletConnectionLogger,
  useErrorLogger,
  useUserActionLogger,
} from "app/hooks/useLogger";
import { WalletType } from "app/types/logging.types";
import {
  emitWalletConnectAttempt,
  emitWalletConnectSuccess,
  emitWalletConnectError,
  forceFlushWalletLogs,
} from "app/utils/wallet-event-emitter";

export const ConnectButton = () => {
  const { open } = useAppKit();
  const [showMobileGuidance, setShowMobileGuidance] = useState(false);
  const [connectionAttempts, setConnectionAttempts] = useState(0);
  const [isRetrying, setIsRetrying] = useState(false);
  const [isConnectFlowPending, setIsConnectFlowPending] = useState(false);
  const connectFlowPendingRef = useRef(false);
  const {
    isMobile,
    hasWalletExtension,
    isMobileWithoutExtension,
    walletType,
    isOKXBrowser,
  } = useDeviceDetection();
  const {
    connect: connectMetaMask,
    isConnecting,
    error,
  } = useMetaMaskConnection();

  // Logging hooks
  const {
    logConnectionAttempt,
    logConnectionSuccess,
    logConnectionError,
    logProviderConflict,
    logWalletDetected,
  } = useWalletConnectionLogger();
  const { logJavaScriptError } = useErrorLogger();
  const { logButtonClick } = useUserActionLogger();

  // Log wallet detection on component mount
  useEffect(() => {
    const detectedWalletType = walletType as WalletType;
    if (detectedWalletType && detectedWalletType !== "unknown") {
      logWalletDetected(detectedWalletType, {
        hasWalletExtension,
        isMobile,
        isOKXBrowser,
        deviceInfo: {
          isMobile,
          hasWalletExtension,
          isMobileWithoutExtension,
          isOKXBrowser,
        },
      });
    }

    // Log provider conflicts if detected
    const multipleWallets = (window as any).__PROVIDER_CONFLICT_DETECTED__;
    if (multipleWallets) {
      logProviderConflict(["multiple_providers"], {
        conflictDetected: true,
        detectedWalletType,
        hasWalletExtension,
      });
    }
  }, [
    walletType,
    hasWalletExtension,
    isMobile,
    isOKXBrowser,
    isMobileWithoutExtension,
    logWalletDetected,
    logProviderConflict,
  ]);

  const connectWithOKX = async () => {
    const walletType: WalletType = "okx";

    try {
      logConnectionAttempt(walletType, {
        method: "okx_direct",
        attemptNumber: connectionAttempts,
        isOKXBrowser,
        hasWalletExtension,
      });

      const okxWallet = (window as any).okxwallet;
      if (!okxWallet) {
        const error = new Error("OKX Wallet not found");
        logConnectionError(walletType, error, {
          method: "okx_direct",
          reason: "wallet_not_found",
          attemptNumber: connectionAttempts,
        });
        throw error;
      }

      // First try to clear any pending requests by checking accounts
      try {
        await okxWallet.request({ method: "eth_accounts" });
      } catch (error) {
        console.log("OKX: Cleared pending state check:", error);
      }

      // Small delay to ensure OKX Wallet is ready
      await new Promise((resolve) => setTimeout(resolve, 200));

      // Request account access
      const accounts = await okxWallet.request({
        method: "eth_requestAccounts",
      });

      if (accounts && accounts.length > 0) {
        console.log("OKX Wallet connected successfully");

        logConnectionSuccess(walletType, {
          method: "okx_direct",
          address: accounts[0],
          attemptNumber: connectionAttempts,
          accountsCount: accounts.length,
        });

        // Emit wallet connection success event
        emitWalletConnectSuccess({
          address: accounts[0],
          connector: "okx",
          chainId: undefined, // OKX doesn't provide chainId in this response
        });

        // Force flush logs after successful connection
        setTimeout(forceFlushWalletLogs, 100);

        // Trigger AppKit to recognize the connection
        await open();
        return true;
      } else {
        const error = new Error("No accounts returned from OKX Wallet");
        logConnectionError(walletType, error, {
          method: "okx_direct",
          reason: "no_accounts_returned",
          attemptNumber: connectionAttempts,
        });

        // Emit wallet connection error event
        emitWalletConnectError({
          error,
          connector: "okx",
        });

        // Force flush logs after error
        setTimeout(forceFlushWalletLogs, 100);

        throw error;
      }
    } catch (error: any) {
      console.error("OKX Wallet connection failed:", error);

      if (!error.logged) {
        logConnectionError(walletType, error, {
          method: "okx_direct",
          reason: "connection_failed",
          attemptNumber: connectionAttempts,
          errorMessage: error.message,
        });

        // Emit wallet connection error event
        emitWalletConnectError({
          error,
          connector: "okx",
        });

        // Force flush logs after error
        setTimeout(forceFlushWalletLogs, 100);
      }

      throw error;
    }
  };

  const handleConnect = async () => {
    if (connectFlowPendingRef.current || isConnecting || isRetrying) {
      return;
    }

    connectFlowPendingRef.current = true;
    setIsConnectFlowPending(true);

    const currentAttempt = connectionAttempts + 1;
    setConnectionAttempts(currentAttempt);

    // Reset disconnect flag when user manually clicks Connect
    setUserManuallyDisconnected(false);

    // Log button click
    logButtonClick("connect_wallet", "ConnectButton_connect");

    // Emit wallet connection attempt event
    emitWalletConnectAttempt(walletType as WalletType, {
      attemptNumber: currentAttempt,
      hasWalletExtension,
      isMobile,
      isOKXBrowser,
      userAgent: navigator.userAgent,
    });

    // Signal to provider guard that user initiated this connection
    ethereumProviderGuard.setUserInitiatedConnection(true);

    // Always show guidance for mobile users or when in problematic browsers
    if (isMobile || isOKXBrowser || connectionAttempts > 0) {
      setShowMobileGuidance(true);
    }

    try {
      // Special handling for OKX wallet browser
      if (walletType === "okx" || isOKXBrowser) {
        console.log(
          "Detected OKX wallet, attempting OKX-specific connection..."
        );
        try {
          await connectWithOKX();
          return;
        } catch (okxError) {
          console.error(
            "OKX connection failed, falling back to AppKit:",
            okxError
          );
          // Dispatch OKX error event for error recovery component
          window.dispatchEvent(
            new CustomEvent("okx-error", {
              detail: { error: okxError, method: "connect" },
            })
          );

          // Log fallback attempt
          logConnectionAttempt("appkit" as WalletType, {
            method: "appkit_fallback",
            originalWallet: "okx",
            fallbackReason: "okx_connection_failed",
            attemptNumber: currentAttempt,
          });
        }
      }

      // If MetaMask is detected, try our enhanced connection first
      if (walletType === "metamask" && hasWalletExtension) {
        try {
          logConnectionAttempt("metamask", {
            method: "metamask_direct",
            attemptNumber: currentAttempt,
            hasWalletExtension,
          });

          await connectMetaMask();

          logConnectionSuccess("metamask", {
            method: "metamask_direct",
            attemptNumber: currentAttempt,
          });

          // Emit wallet connection success event
          emitWalletConnectSuccess({
            address: undefined, // MetaMask connection doesn't return address directly
            connector: "metamask",
          });

          // Force flush logs after successful connection
          setTimeout(forceFlushWalletLogs, 100);

          return;
        } catch (error) {
          console.error(
            "MetaMask connection failed, falling back to AppKit:",
            error
          );

          logConnectionError("metamask", error as Error, {
            method: "metamask_direct",
            reason: "connection_failed",
            attemptNumber: currentAttempt,
            fallbackUsed: true,
          });

          // Emit wallet connection error event
          emitWalletConnectError({
            error: error as Error,
            connector: "metamask",
          });

          // Force flush logs after error
          setTimeout(forceFlushWalletLogs, 100);

          const errorCode = (error as any)?.code;
          const errorMessage = (error as Error)?.message || "";
          const shouldNotFallback =
            errorCode === 4001 ||
            errorCode === -32002 ||
            errorMessage.toLowerCase().includes("rejected") ||
            errorMessage.toLowerCase().includes("already pending") ||
            errorMessage.toLowerCase().includes("already in progress");

          if (shouldNotFallback) {
            setShowMobileGuidance(true);
            return;
          }

          // Log fallback attempt
          logConnectionAttempt("appkit" as WalletType, {
            method: "appkit_fallback",
            originalWallet: "metamask",
            fallbackReason: "metamask_connection_failed",
            attemptNumber: currentAttempt,
          });
        }
      }

      // Fallback to AppKit for all other cases
      console.log("Using AppKit for wallet connection...");

      if (!walletType || walletType === "none") {
        logConnectionAttempt("appkit" as WalletType, {
          method: "appkit_primary",
          attemptNumber: currentAttempt,
          reason: "no_specific_wallet_detected",
        });
      }

      await open();
    } catch (error) {
      console.error("Connection failed:", error);

      logJavaScriptError(error as Error, "ConnectButton_handleConnect");

      setShowMobileGuidance(true);
    } finally {
      // Clear the user initiated flag after a delay
      setTimeout(() => {
        ethereumProviderGuard.setUserInitiatedConnection(false);
      }, 10000);
      connectFlowPendingRef.current = false;
      setIsConnectFlowPending(false);
    }
  };

  const handleRetryConnection = async () => {
    setIsRetrying(true);

    // Log retry attempt
    logButtonClick("retry_connection", "ConnectButton_retry");

    try {
      // Wait a moment before retrying
      await new Promise((resolve) => setTimeout(resolve, 1000));
      await handleConnect();
    } finally {
      setIsRetrying(false);
    }
  };

  const getGuidanceMessage = () => {
    // Check if multiple wallets are detected
    const multipleWallets = (window as any).__PROVIDER_CONFLICT_DETECTED__;

    if (multipleWallets && connectionAttempts > 1) {
      return "Multiple wallet extensions detected causing conflicts. Consider disabling one wallet extension temporarily (like Phantom if you want to use Rabby). Check the warning message above for detailed instructions.";
    } else if (isOKXBrowser && !hasWalletExtension) {
      return "OKX browser detected but wallet not accessible. Please ensure OKX Wallet is enabled in browser settings, or try refreshing the page.";
    } else if (isOKXBrowser && hasWalletExtension) {
      return "OKX Wallet detected! Click Connect and approve the connection in OKX Wallet popup. If popup doesn't appear, check if it's blocked.";
    } else if (isMobileWithoutExtension) {
      return "No wallet detected. Please use WalletConnect to connect your mobile wallet app (like MetaMask mobile or OKX Wallet mobile). The wallet selection will appear after clicking Connect.";
    } else if (isMobile && hasWalletExtension) {
      return `${walletType.toUpperCase()} wallet detected! Click Connect and check your wallet app if a popup doesn't appear.`;
    } else if (!hasWalletExtension) {
      return "No wallet detected. Please install MetaMask browser extension, OKX Wallet, or use WalletConnect for mobile wallets.";
    } else if (connectionAttempts > 1) {
      return `Connection issues detected. Try: 1) Check if ${walletType.toUpperCase()} popup is blocked, 2) If you have multiple wallets (like Phantom + Rabby), disable one temporarily, 3) Refresh the page, or 4) Use the Retry button below.`;
    } else if (walletType === "generic" && multipleWallets) {
      return "Multiple wallets detected. If Phantom keeps popping up when you want to use Rabby, try disabling Phantom extension temporarily. Click Connect when ready.";
    } else if (error) {
      return error;
    }
    return `${
      walletType !== "none" ? walletType.toUpperCase() + " wallet" : "Wallet"
    } detected. Click Connect to access your Web3 wallet.`;
  };

  return (
    <>
      <Button
        onClick={handleConnect}
        type="button"
        disabled={isConnecting || isRetrying || isConnectFlowPending}
      >
        {isConnecting || isRetrying || isConnectFlowPending
          ? "Connecting..."
          : "Connect Wallet"}
      </Button>

      {/* Show retry button for repeated failures */}
      {connectionAttempts > 1 &&
        !isConnecting &&
        !isRetrying &&
        !isConnectFlowPending && (
          <Button
            onClick={handleRetryConnection}
            type="button"
            style={{ marginTop: "8px", fontSize: "14px" }}
          >
            Retry Connection
          </Button>
        )}

      <Notification
        visible={showMobileGuidance}
        message={getGuidanceMessage()}
        type={connectionAttempts > 1 ? "error" : "info"}
        onClose={() => setShowMobileGuidance(false)}
        duration={isOKXBrowser || connectionAttempts > 1 ? 10000 : 7000}
      />
      <MetaMaskErrorRecovery />
      <OKXErrorRecovery />
      <PhantomErrorRecovery />
      <WalletConflictWarning />
    </>
  );
};
